import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtErr } from "../../lib/api";
import { ArrowLeft, ArrowRight, ArrowSquareOut, Sparkle, CheckCircle, Warning, XCircle, FileDoc, Copy, MagnifyingGlass, FileText, Stamp, SlidersHorizontal, Plus, Trash, FileXls, DownloadSimple } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import InferPanel from "./InferPanel";

const PRIO = {
  critical: { label: "Critical", color: "var(--c-red)" },
  important: { label: "Important", color: "var(--c-amber)" },
  optional: { label: "Optional", color: "var(--c-muted)" },
};

export default function ParseStep({ step, setStep, projectId, pack, specs, setSpecs, parseResults, setParseResults, focus = "", suggest = "", suggestWhy = "" }) {
  const [rec, setRec] = useState(null);          // validation-engine recommendation for the focused field
  const [parsing, setParsing] = useState(false);
  const [jobStep, setJobStep] = useState(null);
  const [gaps, setGaps] = useState(null);
  const [gapMeta, setGapMeta] = useState(null);
  const [gapFilter, setGapFilter] = useState("all");
  const [gapAction, setGapAction] = useState("all");
  const [gapSearch, setGapSearch] = useState("");
  const [gapBusy, setGapBusy] = useState(false);
  const [tab, setTab] = useState("merged");
  const [fieldCfg, setFieldCfg] = useState(null);
  const [showCustomize, setShowCustomize] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const addFormRef = useRef(null);
  const [addForm, setAddForm] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState("merge");
  const [importFile, setImportFile] = useState(null);
  const mounted = useRef(true);

  const loadFields = async () => {
    if (!projectId) return;
    try { const { data } = await api.get(`/projects/${projectId}/parse-fields`); setFieldCfg(data); }
    catch { setFieldCfg(null); }
  };
  useEffect(() => { loadFields(); }, [projectId]);
  useEffect(() => {
    if (addForm) addFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [addForm]);
  useEffect(() => {
    if (step !== 3 || !projectId) return;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/gaps`);
        if (data.items) {
          setGaps(data.items);
          setGapMeta({ ran_at: data.ran_at, stale: !!data.stale });
        }
      } catch { /* no stored gap run */ }
    })();
  }, [step, projectId]);

  const gapActions = useMemo(() => [...new Set((gaps || []).map((g) => g.action).filter(Boolean))], [gaps]);
  const filteredGaps = useMemo(() => {
    let list = gaps || [];
    if (gapFilter !== "all") list = list.filter((g) => g.priority === gapFilter);
    if (gapAction !== "all") list = list.filter((g) => g.action === gapAction);
    if (gapSearch.trim()) {
      const q = gapSearch.trim().toLowerCase();
      list = list.filter((g) => (g.label || "").toLowerCase().includes(q)
        || (g.parameter || "").toLowerCase().includes(q) || (g.reason || "").toLowerCase().includes(q));
    }
    return list;
  }, [gaps, gapFilter, gapAction, gapSearch]);

  const activeSections = useMemo(() => {
    if (fieldCfg) return fieldCfg.sections.map((s) => ({ ...s, fields: s.fields.filter((f) => f.included) })).filter((s) => s.fields.length);
    return pack?.sections || [];
  }, [fieldCfg, pack]);
  const allFields = useMemo(() => activeSections.flatMap((s) => s.fields.map((f) => ({ ...f, section: s.name }))), [activeSections]);
  const labelById = useMemo(() => Object.fromEntries(allFields.map((f) => [f.id, f.label])), [allFields]);

  useEffect(() => {
    if (!focus) return;
    const t = setTimeout(() => {
      document.querySelector(`[data-testid="parse-field-${focus}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 350);
    return () => clearTimeout(t);
  }, [focus, pack]);

  // the recommended value for the field we were sent to: taken from the link when the dialog
  // carried one, otherwise asked of the validation engine
  useEffect(() => {
    setRec(null);
    if (!focus) return;
    if (suggest !== "") {
      const n = Number(suggest);
      const f = allFields.find((x) => x.id === focus);
      if (Number.isNaN(n) && (f?.type === "number" || f?.type === undefined)) return;
      setRec({ field: focus, suggested: Number.isNaN(n) ? suggest : n,
               reason: suggestWhy || "proposed by the check that sent you here" });
      return;
    }
    if (!projectId) return;
    api.get(`/validation/${projectId}/recommendations`).then(({ data }) => {
      const hit = (data.spec_changes || []).find((c) => c.field === focus);
      if (hit && String(specs[focus] ?? "") !== String(hit.suggested)) {
        setRec({ field: focus, suggested: hit.suggested, unit: hit.unit,
                 reason: `${hit.check_id} — ${hit.reason}` });
      }
    }).catch(() => {});
  }, [focus, suggest, suggestWhy, projectId, allFields]);

  const applyRec = () => {
    if (!rec) return;
    setSpec(rec.field, rec.suggested);
    toast.success(`${labelById[rec.field] || rec.field} set to ${rec.suggested} — Finalize and re-run Validation`);
    setRec(null);
  };

  const summary = useMemo(() => {
    if (!parseResults) return null;
    let found = 0, conflicts = 0, missing = 0;
    allFields.forEach((f) => {
      const r = parseResults.fields?.[f.id];
      if (!r || r.status === "not_found") missing++;
      else if (r.status === "conflict") conflicts++;
      else found++;
    });
    return { found, conflicts, missing, total: allFields.length };
  }, [parseResults, allFields]);

  const progressPct = useMemo(() => {
    if (!parsing) return 0;
    if (!jobStep || !jobStep.total_files) return 5;
    const perFile = 100 / jobStep.total_files;
    const inFile = jobStep.total_batches ? (jobStep.batch / jobStep.total_batches) : 0;
    return Math.min(99, Math.round(((jobStep.file_index - 1) + inFile) * perFile));
  }, [parsing, jobStep]);

  const pollJob = async (jobId) => {
    for (;;) {
      await new Promise((r) => setTimeout(r, 2000));
      const { data } = await api.get(`/projects/${projectId}/parse/jobs/${jobId}`);
      if (mounted.current) setJobStep(data.step);
      if (data.status === "done") return data.result;
      if (data.status === "error") throw new Error(data.error || "Parse failed");
    }
  };

  const runParse = async () => {
    setParsing(true);
    setJobStep(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/parse`);
      const result = await pollJob(data.job_id);
      setParseResults(result);
      const auto = { ...specs };
      Object.entries(result.fields || {}).forEach(([fid, r]) => {
        if (r.status === "found" && r.value !== null && r.value !== undefined) auto[fid] = r.value;
      });
      setSpecs(auto);
      setTab("merged");
      toast.success(`Parsed: ${result.summary.found} found, ${result.summary.conflicts} conflicts`);
      if (result.batch_errors?.length) toast.warning(`${result.batch_errors.length} batch(es) failed — re-run parse to retry`, { duration: 6000 });
    } catch (e) { toast.error(fmtErr(e), { duration: 8000 }); }
    finally { setParsing(false); setJobStep(null); }
  };

  const resolveConflict = async (fid, src) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/parse/resolve`, { field_id: fid, file_id: src.file_id, value: src.value });
      setParseResults((pr) => ({ ...pr, fields: { ...pr.fields, [fid]: data.field }, summary: data.summary }));
      setSpecs((s) => ({ ...s, [fid]: src.value }));
      toast.success(`${labelById[fid] || fid}: set to ${typeof src.value === "object" ? JSON.stringify(src.value) : src.value}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const saveSpecs = async () => {
    try {
      await api.put(`/projects/${projectId}/specs`, { specs });
      setGapMeta((m) => (m ? { ...m, stale: true } : m));
      toast.success("Specifications saved");
      return true;
    } catch (e) { toast.error(fmtErr(e)); return false; }
  };

  const runGaps = async () => {
    setGapBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/gaps`);
      setGaps(data.items);
      setGapMeta({ ran_at: new Date().toISOString(), stale: false });
      setGapFilter("all"); setGapAction("all"); setGapSearch("");
      if (data.items.length === 0) toast.success("No gaps — all fields covered");
    } catch (e) { toast.error(fmtErr(e), { duration: 8000 }); }
    finally { setGapBusy(false); }
  };

  const copyEmail = () => {
    if (!gaps) return;
    const lines = gaps.map((g, i) => `${i + 1}. [${PRIO[g.priority]?.label || g.priority}] ${g.label} (${g.parameter})\n   Reason: ${g.reason}\n   Suggested default: ${g.default_value ?? "—"}\n   Action: ${g.action}`);
    navigator.clipboard.writeText(`Client Query List — missing specification parameters\n\n${lines.join("\n\n")}`);
    toast.success("Query list copied — paste into an email");
  };

  const setSpec = (id, v) => setSpecs({ ...specs, [id]: v });

  const doToggle = async (fid, included) => {
    try {
      await api.patch(`/projects/${projectId}/parse-fields/${fid}`, { included });
      loadFields();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleField = (f, included) => {
    if (!included && f.required) {
      setDeleteImpact({
        fid: f.id, label: f.label, action: "exclude",
        impact: `'${f.label}' is a mandatory parameter in this industry pack. De-selecting it means: it will NOT be extracted during AI parsing, gap analysis will no longer flag it, and project finalization will no longer require it. Design calculations, validation checks or drawings that depend on this parameter may fall back to defaults or be skipped.`,
      });
      return;
    }
    doToggle(f.id, included);
  };

  const deleteField = async (f, force = false) => {
    try {
      await api.delete(`/projects/${projectId}/parse-fields/${f.id}`, { params: { force } });
      toast.success(`${f.label} removed from parse criteria`);
      setDeleteImpact(null);
      loadFields();
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.impact) {
        setDeleteImpact({ fid: f.id, label: f.label, impact: e.response.data.impact, action: "delete" });
      } else toast.error(fmtErr(e));
    }
  };

  const addField = async () => {
    if (!addForm?.label?.trim()) { toast.error("Label is required"); return; }
    try {
      await api.post(`/projects/${projectId}/parse-fields`, {
        ...addForm,
        options: addForm.options ? addForm.options.split(",").map((x) => x.trim()).filter(Boolean) : undefined,
      });
      toast.success("Custom field added");
      setAddForm(null);
      loadFields();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const importExcel = async () => {
    if (!importFile) { toast.error("Choose an .xlsx file first"); return; }
    const fd = new FormData();
    fd.append("file", importFile);
    try {
      const { data } = await api.post(`/projects/${projectId}/parse-fields/import?mode=${importMode}`, fd);
      toast.success(`Import (${data.mode}): ${data.created} added, ${data.updated} updated, ${data.reactivated} re-included, ${data.excluded} excluded${data.values_applied ? ` · ${data.values_applied} spec values applied` : ""}`);
      setImportOpen(false);
      setImportFile(null);
      loadFields();
      if (data.values_applied) {
        const proj = await api.get(`/projects/${projectId}`);
        setSpecs(proj.data.specs || {});
      }
    } catch (e) { toast.error(fmtErr(e), { duration: 8000 }); }
  };

  const openSource = (fileId, page, quote, filename) => {
    if (!fileId) return;
    const isPdf = (filename || "").toLowerCase().endsWith(".pdf");
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}`;
    if (isPdf && (page || quote)) {
      const u = `${base}/highlight?page=${page || 1}&quote=${encodeURIComponent((quote || "").slice(0, 300))}`;
      window.open(page ? `${u}#page=${page}` : u, "_blank");
      return;
    }
    window.open(page ? `${base}/download#page=${page}` : `${base}/download`, "_blank");
  };

  const widget = (f) => {
    const raw = specs[f.id] ?? f.default;
    if (f.type === "boolean") {
      return <Switch data-testid={`parse-input-${f.id}`} checked={!!raw} onCheckedChange={(v) => setSpec(f.id, v)} />;
    }
    if (f.type === "select") {
      return (
        <Select value={raw !== undefined && raw !== null && raw !== "" ? String(raw) : ""} onValueChange={(v) => setSpec(f.id, v)}>
          <SelectTrigger data-testid={`parse-select-${f.id}`} className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
            {(f.options || []).map((o) => <SelectItem key={o} value={o} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === "multi-select") {
      const arr = Array.isArray(raw) ? raw : [];
      return (
        <div className="flex flex-wrap gap-1" data-testid={`parse-input-${f.id}`}>
          {(f.options || []).map((o) => {
            const on = arr.includes(o);
            return (
              <button key={o} type="button" onClick={() => setSpec(f.id, on ? arr.filter((x) => x !== o) : [...arr, o])}
                className={`px-1.5 py-0.5 rounded-sm text-[10px] border ${on ? "bg-[var(--c-accent)]/15 border-[var(--c-accent)] text-[var(--c-text)]" : "border-[var(--c-border)] text-[var(--c-muted)]"}`}>
                {o}
              </button>
            );
          })}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5">
        <Input data-testid={`parse-input-${f.id}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} step="any"
          value={raw ?? ""} onChange={(e) => setSpec(f.id, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
          className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs font-mono" />
        {f.unit && <span className="font-mono text-[10px] text-[var(--c-muted)] shrink-0 w-10">{f.unit}</span>}
      </div>
    );
  };

  const statusChip = (f) => {
    const r = parseResults?.fields?.[f.id];
    if (!parseResults) return null;
    if (!r || r.status === "not_found")
      return <span className="inline-flex items-center gap-1 text-[10px] text-[var(--c-red)]" data-testid={`status-notfound-${f.id}`}><XCircle size={11} weight="fill" /> Not found</span>;
    if (r.status === "conflict")
      return <span className="inline-flex items-center gap-1 text-[10px] text-[var(--c-amber)]" data-testid={`status-conflict-${f.id}`}><Warning size={11} weight="fill" /> Conflict — pick below</span>;
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-[var(--c-green)]" data-testid={`status-found-${f.id}`}>
        <CheckCircle size={11} weight="fill" /> {r.resolved ? "Resolved" : "Found"}{r.confidence != null && <span className="font-mono">{Math.round(r.confidence * 100)}%</span>}
      </span>
    );
  };

  const cite = (r, fid, fileId, filename) => {
    if (!r || (r.page == null && !r.section)) return null;
    const label = [r.page != null ? `p.${r.page}` : null, r.section ? `§ ${r.section}` : null].filter(Boolean).join(" · ");
    const srcId = fileId || r.file_id;
    return (
      <button type="button" data-testid={`cite-${fid}`} onClick={() => openSource(srcId, r.page, r.quote, filename || r.source)}
        title={r.quote ? `"${r.quote}" — click to open the document at this text` : "Open source document"}
        className="inline-flex items-center gap-1 text-[9px] font-mono px-1 py-0.5 rounded-sm border border-[var(--c-accent)]/40 text-[var(--c-accent)] bg-[var(--c-accent)]/10 hover:bg-[var(--c-accent)]/25 transition-colors duration-150 cursor-pointer max-w-52">
        <FileText size={9} className="shrink-0" /> <span className="truncate">{label}</span>
        <ArrowSquareOut size={9} className="shrink-0" />
      </button>
    );
  };

  const kindBadge = (kind) => (
    <span className={`px-1 py-0.5 rounded-sm border font-mono uppercase text-[8px] tracking-wider ${
      kind === "corrigendum" ? "text-[var(--c-amber)] border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10" : "text-[var(--c-muted)] border-[var(--c-border)]"}`}>
      {kind === "corrigendum" ? "Corrigendum" : "Base"}
    </span>
  );

  const tabCls = (active) => `inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border text-[11px] transition-colors duration-150 ${
    active ? "border-[var(--c-accent)] bg-[var(--c-accent)]/15 text-[var(--c-text)]" : "border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)]"}`;

  const fileView = (pf) => {
    const diffChanged = new Set((pf.diff?.changed || []).map((d) => d.field));
    const diffAdded = new Set((pf.diff?.added || []).map((d) => d.field));
    const rows = allFields.map((f) => ({ f, r: (pf.fields || {})[f.id] })).filter((x) => x.r);
    return (
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-hidden" data-testid={`doc-view-${pf.file_id.slice(0, 8)}`}>
        <div className="px-4 py-2.5 border-b border-[var(--c-border)] flex items-center gap-3 flex-wrap">
          <span className="text-xs font-medium text-[var(--c-text)]">{pf.filename}</span>
          {kindBadge(pf.doc_kind)}
          <span className="font-mono text-[10px] text-[var(--c-muted)]">{pf.found} fields extracted</span>
          {pf.diff && (
            <span className="font-mono text-[10px]" data-testid={`diff-summary-${pf.file_id.slice(0, 8)}`}>
              <span className="text-[var(--c-amber)]">{pf.diff.changed.length} changed</span>
              <span className="text-[var(--c-muted)]"> · </span>
              <span className="text-[var(--c-green)]">{pf.diff.added.length} added</span>
              <span className="text-[var(--c-muted)]"> vs earlier documents</span>
            </span>
          )}
        </div>
        {pf.diff?.changed?.length > 0 && (
          <div className="px-4 py-2.5 border-b border-[var(--c-border)] bg-[var(--c-amber)]/5">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--c-amber)] mb-1.5">Changed by this corrigendum</div>
            {pf.diff.changed.map((d) => (
              <div key={d.field} className="flex items-center gap-2 text-[11px] py-0.5" data-testid={`diff-changed-${d.field}`}>
                <span className="text-[var(--c-muted)] w-56 truncate shrink-0">{labelById[d.field] || d.field}</span>
                <span className="font-mono text-[var(--c-red)] line-through">{String(d.prior)}</span>
                <span className="text-[var(--c-muted)]">→</span>
                <span className="font-mono text-[var(--c-green)]">{String(d.value)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--c-border)]">
                {["Parameter", "Value", "Conf.", "Location", ""].map((h, i) => (
                  <th key={i} className="text-left px-4 py-2 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ f, r }) => (
                <tr key={f.id} className="border-b border-[var(--c-border)]/60 last:border-0" data-testid={`doc-field-${pf.file_id.slice(0, 8)}-${f.id}`}>
                  <td className="px-4 py-2">
                    <div className="text-xs text-[var(--c-text)]">{f.label}</div>
                    <div className="font-mono text-[10px] text-[var(--c-accent)]">{f.id}</div>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-[var(--c-text)] max-w-48 truncate" title={typeof r.value === "object" ? JSON.stringify(r.value) : String(r.value)}>
                    {typeof r.value === "object" ? JSON.stringify(r.value) : String(r.value)}
                  </td>
                  <td className="px-4 py-2 font-mono text-[10px] text-[var(--c-muted)]">{r.confidence != null ? `${Math.round(r.confidence * 100)}%` : "—"}</td>
                  <td className="px-4 py-2">{cite(r, `${pf.file_id.slice(0, 8)}-${f.id}`, pf.file_id, pf.filename)}</td>
                  <td className="px-4 py-2">
                    {diffChanged.has(f.id) && <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm border border-[var(--c-amber)]/40 text-[var(--c-amber)]">Changed</span>}
                    {diffAdded.has(f.id) && <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm border border-[var(--c-green)]/40 text-[var(--c-green)]">Added</span>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-xs text-[var(--c-muted)]">No parameters extracted from this document.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (step === 2) {
    const activeFile = tab !== "merged" ? (parseResults?.by_file || []).find((p) => p.file_id === tab) : null;
    return (
      <div className="space-y-4" data-testid="np-step-parse">
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-sm font-medium text-[var(--c-text)]">AI Parsing · {pack?.name || "—"}</div>
              <div className="text-xs text-[var(--c-muted)] mt-0.5">
                Each document is parsed separately ({allFields.length} parameters, batched) with page/section provenance; re-parsing always covers all uploaded documents
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" data-testid="customize-fields-btn" onClick={() => setShowCustomize((v) => !v)}
                className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">
                <SlidersHorizontal size={14} className="mr-1.5" /> Fields
              </Button>
              <Button data-testid="parse-run-btn" onClick={runParse} disabled={parsing}
                className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
                <Sparkle size={14} className="mr-1.5" /> {parsing ? "Parsing with Cognara AI…" : parseResults ? "Re-parse All Documents" : "Parse Documents"}
              </Button>
            </div>
          </div>
          {parsing && (
            <div className="mt-4" data-testid="parse-progress">
              <div className="flex justify-between text-[10px] font-mono text-[var(--c-muted)] mb-1">
                <span data-testid="parse-progress-text">
                  {jobStep?.file_name
                    ? jobStep.phase === "scan"
                      ? `Scanning ${jobStep.file_name} — file ${jobStep.file_index}/${jobStep.total_files}, chunk ${jobStep.batch}/${jobStep.total_batches}`
                      : `Extracting from ${jobStep.file_name} — file ${jobStep.file_index}/${jobStep.total_files}, batch ${jobStep.batch}/${jobStep.total_batches}`
                    : "Starting parse job…"}
                </span>
                <span>{progressPct}%</span>
              </div>
              <div className="h-1.5 bg-[var(--c-border)] rounded-sm overflow-hidden">
                <div className="h-full bg-[var(--c-accent)] transition-all duration-500" style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          )}
          {summary && !parsing && (
            <div className="mt-4 flex items-center gap-4 font-mono text-xs border border-[var(--c-border)] rounded-sm px-4 py-2.5 bg-[var(--c-bg)]" data-testid="parse-summary">
              <span className="text-[var(--c-green)]">Found: {summary.found}/{summary.total}</span>
              <span className="text-[var(--c-amber)]">Conflicts: {summary.conflicts}</span>
              <span className="text-[var(--c-red)]">Missing: {summary.missing}</span>
              {(parseResults?.skipped_files || []).length > 0 && (
                <span className="text-[var(--c-muted)]" title={parseResults.skipped_files.join(", ")}>Skipped files: {parseResults.skipped_files.length}</span>
              )}
            </div>
          )}
        </div>

        <InferPanel projectId={projectId} setSpecs={setSpecs} />

        {showCustomize && fieldCfg && (
          <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm" data-testid="customize-panel">
            <div className="px-4 py-2.5 border-b border-[var(--c-border)] flex items-center justify-between flex-wrap gap-2">
              <div>
                <span className="text-xs font-medium text-[var(--c-text)]">Parse Criteria</span>
                <span className="font-mono text-[10px] text-[var(--c-muted)] ml-2">
                  {allFields.length} of {fieldCfg.sections.reduce((n, s) => n + s.fields.length, 0)} fields included
                </span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" data-testid="template-download-btn"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/parse-fields/template`, "_blank")}
                  className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-7 text-[11px]">
                  <DownloadSimple size={12} className="mr-1" /> Excel Template
                </Button>
                <Button variant="outline" size="sm" data-testid="import-excel-btn" onClick={() => setImportOpen(true)}
                  className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-7 text-[11px]">
                  <FileXls size={12} className="mr-1" /> Import Excel
                </Button>
                <Button variant="outline" size="sm" data-testid="cfg-add-toggle"
                  onClick={() => setAddForm(addForm ? null : { section: "Custom Fields", label: "", type: "text", unit: "", options: "", required: false, ai_hint: "" })}
                  className="border-[var(--c-accent)]/50 text-[var(--c-accent)] hover:bg-[var(--c-accent)]/10 rounded-sm h-7 text-[11px]">
                  <Plus size={12} className="mr-1" /> {addForm ? "Close" : "Add Field"}
                </Button>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {fieldCfg.sections.map((sec) => (
                <div key={sec.id || sec.name}>
                  <div className="px-4 py-1.5 bg-[var(--c-bg)] text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)]">{sec.name}</div>
                  {sec.fields.map((f) => (
                    <div key={f.id} className={`flex items-center gap-2.5 px-4 py-1.5 border-t border-[var(--c-border)]/50 ${f.included ? "" : "opacity-45"}`} data-testid={`cfg-field-${f.id}`}>
                      <input type="checkbox" data-testid={`cfg-include-${f.id}`} checked={!!f.included} disabled={f.custom}
                        onChange={(e) => toggleField(f, e.target.checked)}
                        className="accent-[var(--c-accent)] cursor-pointer disabled:cursor-not-allowed" />
                      <span className="text-xs text-[var(--c-text)] flex-1 truncate">
                        {f.label}{f.required && <span className="text-[var(--c-red)] ml-0.5">*</span>}
                        {f.custom && <span className="ml-1.5 text-[8px] font-mono uppercase px-1 py-0.5 rounded-sm border border-[var(--c-green)]/40 text-[var(--c-green)]">Custom</span>}
                      </span>
                      <span className="font-mono text-[9px] text-[var(--c-muted)]">{f.type}{f.unit ? ` · ${f.unit}` : ""}</span>
                      <button type="button" data-testid={`cfg-delete-${f.id}`} onClick={() => deleteField(f)}
                        className="p-1 text-[var(--c-muted)] hover:text-[var(--c-red)] transition-colors duration-150">
                        <Trash size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {addForm && (
              <div ref={addFormRef} className="border-t border-[var(--c-border)] p-4 grid grid-cols-2 md:grid-cols-4 gap-2 items-end" data-testid="cfg-add-form">
                <div className="space-y-1">
                  <label className="text-[10px] text-[var(--c-muted)]">Section</label>
                  <Select value={addForm.section} onValueChange={(v) => setAddForm({ ...addForm, section: v })}>
                    <SelectTrigger data-testid="cfg-add-section" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                      {[...new Set([...fieldCfg.sections.map((s) => s.name), "Custom Fields"])].map((n) => (
                        <SelectItem key={n} value={n} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-[var(--c-muted)]">Label *</label>
                  <Input data-testid="cfg-add-label" value={addForm.label} onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" placeholder="e.g. Altitude limit" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-[var(--c-muted)]">Type</label>
                  <Select value={addForm.type} onValueChange={(v) => setAddForm({ ...addForm, type: v })}>
                    <SelectTrigger data-testid="cfg-add-type" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                      {["text", "number", "select", "multi-select", "boolean", "date"].map((t) => (
                        <SelectItem key={t} value={t} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] text-[var(--c-muted)]">Unit</label>
                  <Input data-testid="cfg-add-unit" value={addForm.unit} onChange={(e) => setAddForm({ ...addForm, unit: e.target.value })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" placeholder="kV, mm…" />
                </div>
                {(addForm.type === "select" || addForm.type === "multi-select") && (
                  <div className="space-y-1 col-span-2">
                    <label className="text-[10px] text-[var(--c-muted)]">Options (comma-separated)</label>
                    <Input data-testid="cfg-add-options" value={addForm.options} onChange={(e) => setAddForm({ ...addForm, options: e.target.value })}
                      className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" placeholder="ONAN, ONAF" />
                  </div>
                )}
                <div className="space-y-1 col-span-2">
                  <label className="text-[10px] text-[var(--c-muted)]">AI hint (where to look in documents)</label>
                  <Input data-testid="cfg-add-hint" value={addForm.ai_hint} onChange={(e) => setAddForm({ ...addForm, ai_hint: e.target.value })}
                    className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-8 text-xs" placeholder="Look for…" />
                </div>
                <label className="flex items-center gap-2 text-[11px] text-[var(--c-muted)]">
                  <Switch data-testid="cfg-add-required" checked={addForm.required} onCheckedChange={(v) => setAddForm({ ...addForm, required: v })} /> Mandatory
                </label>
                <div className="flex gap-2">
                  <Button data-testid="cfg-add-save" onClick={addField} className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">Add Field</Button>
                  <Button variant="outline" onClick={() => setAddForm(null)} className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {parseResults?.by_file?.length > 0 && (
          <div className="flex gap-1.5 flex-wrap" data-testid="parse-doc-tabs">
            <button data-testid="doc-tab-merged" onClick={() => setTab("merged")} className={tabCls(tab === "merged")}>
              Merged view
            </button>
            {parseResults.by_file.map((pf) => (
              <button key={pf.file_id} data-testid={`doc-tab-${pf.file_id.slice(0, 8)}`} onClick={() => setTab(pf.file_id)} className={tabCls(tab === pf.file_id)}>
                {pf.doc_kind === "corrigendum" && <Stamp size={11} className="text-[var(--c-amber)]" />}
                <span className="max-w-44 truncate">{pf.filename}</span>
                <span className="font-mono text-[10px] text-[var(--c-green)]">{pf.found}</span>
              </button>
            ))}
          </div>
        )}

        {activeFile ? fileView(activeFile) : (
          <>
            {(pack?.sections || []).map((s) => (
              <div key={s.id} className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm" data-testid={`parse-section-${s.id}`}>
                <div className="px-4 py-2.5 border-b border-[var(--c-border)] text-xs font-medium text-[var(--c-text)]">{s.name}</div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  {s.fields.map((f) => {
                    const r = parseResults?.fields?.[f.id];
                    return (
                      <div key={f.id} className={`space-y-1 ${f.id === focus
                        ? "ring-2 ring-[var(--c-accent)] rounded-sm bg-[var(--c-accent)]/10 p-2 -m-1" : ""}`}
                        data-testid={`parse-field-${f.id}`}>
                        {f.id === focus && (
                          <div className="text-[10px] text-[var(--c-accent)]" data-testid={`focus-hint-${f.id}`}>
                            You were sent here to fix this field — change it, then Finalize and re-run Validation
                          </div>
                        )}
                        {f.id === focus && rec && rec.suggested != null && (
                          <div className="flex flex-wrap items-center gap-2 rounded-sm border border-[var(--c-green)]/45 bg-[var(--c-green)]/[0.08] px-2 py-1.5"
                            data-testid={`recommended-value-${f.id}`}>
                            <span className="font-mono text-[10.5px] text-[var(--c-text)]">
                              Recommended <span className="text-[var(--c-muted)]">{String(specs[f.id] ?? "—")}</span>
                              {" → "}
                              <span className="text-[var(--c-green)]">{String(rec.suggested)}{f.unit ? ` ${f.unit}` : ""}</span>
                            </span>
                            {rec.reason && <span className="text-[10px] text-[var(--c-muted)] truncate">{rec.reason}</span>}
                            <button type="button" data-testid={`apply-recommended-${f.id}`} onClick={applyRec}
                              className="ml-auto rounded-sm bg-[var(--c-green)] px-2 py-1 text-[10px] font-medium text-white transition-colors duration-150 hover:bg-[var(--c-green)]/85">
                              Apply recommended
                            </button>
                          </div>
                        )}
                        <div className="flex items-center justify-between gap-2">
                          <label className="text-[11px] text-[var(--c-muted)]">{f.label}{f.required && <span className="text-[var(--c-red)] ml-0.5">*</span>}</label>
                          <span className="flex items-center gap-1.5 shrink-0">{cite(r, f.id)}{statusChip(f)}</span>
                        </div>
                        {widget(f)}
                        {r?.status === "conflict" && (
                          <div className="space-y-1 border border-[var(--c-amber)]/30 rounded-sm p-2 bg-[var(--c-amber)]/5" data-testid={`conflict-detail-${f.id}`}>
                            <div className="text-[9px] font-mono uppercase tracking-wider text-[var(--c-amber)]">Conflicting values — pick one</div>
                            {(r.sources || r.conflicts || []).map((c, i) => (
                              <div key={i} className="flex items-center gap-2 text-[10px]" data-testid={`conflict-src-${f.id}-${i}`}>
                                {kindBadge(c.doc_kind)}
                                <span className="text-[var(--c-text)] font-mono">{typeof c.value === "object" ? JSON.stringify(c.value) : String(c.value)}</span>
                                <button type="button" data-testid={`conflict-open-${f.id}-${i}`} onClick={() => openSource(c.file_id, c.page, c.quote, c.filename)}
                                  className="text-[var(--c-muted)] hover:text-[var(--c-accent)] truncate flex-1 text-left transition-colors duration-150"
                                  title={c.quote ? `"${c.quote}" — click to open document${c.page ? ` at page ${c.page}` : ""}` : "Open source document"}>
                                  {c.filename}{c.page != null ? ` · p.${c.page}` : ""}{c.section ? ` · ${c.section}` : ""}
                                </button>
                                <button data-testid={`conflict-pick-${f.id}-${i}`} onClick={() => resolveConflict(f.id, c)}
                                  className="px-1.5 py-0.5 rounded-sm bg-[var(--c-accent)]/15 border border-[var(--c-accent)] text-[var(--c-text)] hover:bg-[var(--c-accent)]/30 transition-colors duration-150">
                                  Use
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {r?.status === "found" && r.source && (
                          <div className="text-[9px] font-mono text-[var(--c-muted)] truncate" title={r.quote ? `"${r.quote}"` : r.source}>
                            from {r.source}
                            {(r.sources || []).length > 1 && <span> (+{r.sources.length - 1} agreeing source{r.sources.length > 2 ? "s" : ""})</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}

        <div className="flex justify-between">
          <Button variant="outline" data-testid="np-back-3" onClick={() => setStep(1)}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">
            <ArrowLeft size={14} className="mr-1.5" /> Back
          </Button>
          <Button data-testid="np-continue-3" onClick={async () => { if (await saveSpecs()) setStep(3); }}
            className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
            Save & Continue <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </div>

        <Dialog open={!!deleteImpact} onOpenChange={() => setDeleteImpact(null)}>
          <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" data-testid="field-delete-impact-dialog">
            <DialogHeader>
              <DialogTitle className="text-[var(--c-text)] text-sm">{deleteImpact?.action === "delete" ? "Remove" : "De-select"} mandatory field “{deleteImpact?.label}”?</DialogTitle>
              <DialogDescription className="sr-only">Impact of removing a mandatory field</DialogDescription>
            </DialogHeader>
            <p className="text-xs text-[var(--c-amber)] leading-relaxed" data-testid="field-delete-impact-text">{deleteImpact?.impact}</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDeleteImpact(null)}
                className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">Cancel</Button>
              <Button data-testid="field-delete-confirm-btn"
                onClick={() => {
                  if (deleteImpact.action === "delete") deleteField({ id: deleteImpact.fid, label: deleteImpact.label }, true);
                  else { doToggle(deleteImpact.fid, false); setDeleteImpact(null); }
                }}
                className="bg-[var(--c-red)] hover:bg-[var(--c-red)]/85 text-[var(--c-text)] rounded-sm h-8 text-xs">{deleteImpact?.action === "delete" ? "Remove anyway" : "De-select anyway"}</Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent className="bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)]" data-testid="import-excel-dialog">
            <DialogHeader>
              <DialogTitle className="text-[var(--c-text)] text-sm">Import parse criteria from Excel</DialogTitle>
              <DialogDescription className="sr-only">Upload an .xlsx criteria file</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-[11px] text-[var(--c-muted)]">Use the <span className="font-medium text-[var(--c-text)]">Excel Template</span> (Fields → Excel Template): fill the <span className="font-medium text-[var(--c-text)]">Specification</span> sheet's Value column to set spec data, and edit the <span className="font-medium text-[var(--c-text)]">Field Configuration</span> sheet to change parse criteria. Legacy single-sheet criteria files (with a Label column) still work.</p>
              <Input type="file" accept=".xlsx" data-testid="import-excel-input"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-9 text-xs" />
              <div className="space-y-1.5">
                <label className="flex items-start gap-2 text-xs text-[var(--c-text)] cursor-pointer" data-testid="import-mode-merge">
                  <input type="radio" name="import-mode" checked={importMode === "merge"} onChange={() => setImportMode("merge")} className="mt-0.5 accent-[var(--c-accent)]" />
                  <span><span className="font-medium">Merge</span> — add/update fields from the file; existing criteria stay untouched</span>
                </label>
                <label className="flex items-start gap-2 text-xs text-[var(--c-text)] cursor-pointer" data-testid="import-mode-replace">
                  <input type="radio" name="import-mode" checked={importMode === "replace"} onChange={() => setImportMode("replace")} className="mt-0.5 accent-[var(--c-amber)]" />
                  <span><span className="font-medium text-[var(--c-amber)]">Replace</span> — pack fields not present in the file are excluded from parsing, gap detection and finalization</span>
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setImportOpen(false)}
                  className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">Cancel</Button>
                <Button data-testid="import-run-btn" onClick={importExcel} disabled={!importFile}
                  className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">Import</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="np-step-gaps">
      <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-sm font-medium text-[var(--c-text)]">Gap Detection</div>
            <div className="text-xs text-[var(--c-muted)] mt-0.5">Cognara AI classifies missing parameters as Critical / Important / Optional for {pack?.name || "this industry"}</div>
            {gapMeta?.ran_at && (
              <div className="text-[10px] font-mono text-[var(--c-muted)] mt-1" data-testid="gaps-last-run">
                Last run: {new Date(gapMeta.ran_at).toLocaleString()}
                {gapMeta.stale && <span className="text-[var(--c-amber)]" data-testid="gaps-stale-badge"> · specs changed since — re-run recommended</span>}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {gaps && gaps.length > 0 && (
              <>
                <Button variant="outline" size="sm" data-testid="gaps-export-docx"
                  onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/gaps/export`, "_blank")}
                  className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
                  <FileDoc size={13} className="mr-1" /> Export as Word
                </Button>
                <Button variant="outline" size="sm" data-testid="gaps-copy-email" onClick={copyEmail}
                  className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
                  <Copy size={13} className="mr-1" /> Copy as Email
                </Button>
              </>
            )}
            <Button data-testid="gaps-run-btn" onClick={runGaps} disabled={gapBusy}
              className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
              <MagnifyingGlass size={13} className="mr-1" /> {gapBusy ? "Analyzing…" : "Detect Gaps"}
            </Button>
          </div>
        </div>
      </div>

      {gaps && gaps.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="gaps-filters">
          {[
            { key: "all", label: "All", color: "var(--c-accent)" },
            { key: "critical", label: "Critical", color: "var(--c-red)" },
            { key: "important", label: "Important", color: "var(--c-amber)" },
            { key: "optional", label: "Optional", color: "var(--c-muted)" },
          ].map((p) => {
            const count = p.key === "all" ? gaps.length : gaps.filter((g) => g.priority === p.key).length;
            const active = gapFilter === p.key;
            return (
              <button key={p.key} data-testid={`gaps-filter-${p.key}`} onClick={() => setGapFilter(p.key)}
                className="px-2.5 py-1 rounded-sm border text-[11px] font-mono transition-colors duration-150"
                style={active
                  ? { borderColor: p.color, color: p.color, backgroundColor: `color-mix(in srgb, ${p.color} 12%, transparent)` }
                  : { borderColor: "var(--c-border)", color: "var(--c-muted)" }}>
                {p.label} · {count}
              </button>
            );
          })}
          <Select value={gapAction} onValueChange={setGapAction}>
            <SelectTrigger data-testid="gaps-action-filter" className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-[11px] w-48 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
              <SelectItem value="all" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">All actions</SelectItem>
              {gapActions.map((a) => <SelectItem key={a} value={a} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="gaps-search" value={gapSearch} onChange={(e) => setGapSearch(e.target.value)}
            placeholder="Search parameter, reason…"
            className="bg-[var(--c-bg)] border-[var(--c-border)] text-[var(--c-text)] h-7 text-[11px] w-56 rounded-sm" />
          <span className="font-mono text-[10px] text-[var(--c-muted)]" data-testid="gaps-filter-count">
            showing {filteredGaps.length} of {gaps.length}
          </span>
        </div>
      )}

      {gaps && (
        <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm overflow-x-auto" data-testid="gaps-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--c-border)]">
                {["#", "Parameter", "Priority", "Reason", "Default Value", "Action"].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredGaps.map((g, i) => (
                <tr key={g.parameter} className="border-b border-[var(--c-border)]/60 last:border-0" data-testid={`gap-row-${g.parameter}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-[var(--c-muted)]">{i + 1}</td>
                  <td className="px-4 py-2.5">
                    <div className="text-xs text-[var(--c-text)]">{g.label}</div>
                    <div className="font-mono text-[10px] text-[var(--c-accent)]">{g.parameter}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm border font-mono uppercase tracking-wider"
                      style={{ color: PRIO[g.priority]?.color, borderColor: `color-mix(in srgb, ${PRIO[g.priority]?.color} 25%, transparent)`, backgroundColor: `color-mix(in srgb, ${PRIO[g.priority]?.color} 10%, transparent)` }}>
                      {PRIO[g.priority]?.label || g.priority}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-[var(--c-muted)] max-w-64">{g.reason}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[var(--c-text)]">{g.default_value ?? "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-[var(--c-muted)]">{g.action}</td>
                </tr>
              ))}
              {gaps.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-[var(--c-green)]" data-testid="gaps-none">No gaps — specification is complete for this industry.</td></tr>
              )}
              {gaps.length > 0 && filteredGaps.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-[var(--c-muted)]" data-testid="gaps-no-match">No gaps match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" data-testid="np-back-4" onClick={() => setStep(2)}
          className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm">
          <ArrowLeft size={14} className="mr-1.5" /> Back
        </Button>
        <Button data-testid="np-continue-4" onClick={() => setStep(4)}
          className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm">
          Continue <ArrowRight size={14} className="ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
